import EditorActions from 'components/Common/EditorActions'
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Link, useHistory } from "react-router-dom"
import { Alert, Button, Card, CardBody, Col, Container, FormGroup, Input, Label, Row } from "reactstrap"
import EditorNavigation from "pages/UserAccounts/EditorNavigation"
import "../phases.scss"
import PhaseActionDialog from "../PhaseActionDialog"
import { invalidateEventCache } from "../../../../../../eventCachePolicy"
import usePhaseContext from "../usePhaseContext"
import usePhaseOperation, { phaseMessage } from "../usePhaseOperation"
import { isTenis10Context } from "../tenis10Phase"
import Tenis10PhaseFields, { initialValue, perGroupOf } from "./Tenis10PhaseFields"

function PhaseEditor({ context, phaseId, userId }) {
  const { eventId, competitionId } = context
  const history = useHistory()
  const phase = context.phases.find(row => row._id === phaseId)
  // A Tenis10 phase takes the first N children of every group, Fixed only (RELEASE-TENIS10 § 11 P51)
  const tenis10 = isTenis10Context(context)
  const initialQuota = tenis10 ? initialValue(phase, context) : phase?.mlNumber == null ? "" : String(phase.mlNumber)
  const [quota, setQuota] = useState(initialQuota)
  const [ranking, setRanking] = useState(phase?.interGroupRanking || "fixed")
  const [review, setReview] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const operation = usePhaseOperation({ eventId, competitionId, phaseId, userId })
  const returnTo = `/events/${eventId}/competitions/${competitionId}/phases`
  useEffect(() => { if (operation.status === "saved") { invalidateEventCache({ eventId, competitionId }); history.push(returnTo) } }, [operation.status, returnTo, history, eventId, competitionId])
  const minimum = Math.max(2, context.numberOfGroups)
  const perGroup = tenis10 ? perGroupOf(quota, context) : null
  const validQuota = tenis10 ? perGroup != null
    : quota !== "" && Number.isInteger(Number(quota)) && Number(quota) >= minimum && Number(quota) <= context.capacity
  const details = { mlNumber: tenis10 ? perGroup * context.numberOfGroups : Number(quota), interGroupRanking: tenis10 ? "fixed" : ranking }
  const drawSize = validQuota ? 2 ** Math.ceil(Math.log2(details.mlNumber)) : null
  // A Tenis10 phase saved with Dynamic (before the rule) is a change to save as soon as its count is valid: it becomes Fixed.
  const dirty = quota !== initialQuota || (tenis10 ? Boolean(phase) && validQuota && (phase.interGroupRanking || "fixed") !== "fixed"
    : ranking !== (phase?.interGroupRanking || "fixed"))
  const permission = phaseId ? phase?.permissions?.edit : context.permissions?.create
  const blocked = !permission?.allowed || Boolean(phaseId && !phase)
  const units = context.gameType === "doubles" ? "teams" : "players"
  return <>
    {operation.status !== "saved" && <EditorNavigation dirty={dirty} pending={operation.pending} onRecover={operation.recover} />}
    <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
      <h4 className="mb-2">{phaseId ? `Edit phase${phase?.phaseId ? ` #${phase.phaseId}` : ""}` : "Create phase"}</h4>
      <Link to={returnTo}>Back to phases</Link>
    </div>
    <Card><CardBody>
      {blocked && <Alert color="warning">{phaseId && !phase ? "This phase is no longer available." : phaseMessage(permission?.reason)}</Alert>}
      <form noValidate onSubmit={event => {
        event.preventDefault(); setSubmitted(true)
        if (blocked || !validQuota || operation.pending) return
        if (phaseId) setReview(true)
        else operation.save("create", context.revision, details)
      }}>
        <fieldset disabled={blocked || operation.pending}>
          <Row><Col md={7} lg={6}>
            <p className="mb-1 font-weight-semibold">Eliminatory phase</p>
            <p className="text-muted">Qualifiers from the competition groups continue in a knockout draw.</p>
            {tenis10 ? <Tenis10PhaseFields value={quota} onChange={setQuota} invalid={submitted && !validQuota} context={context} phase={phase} /> : <>
            <FormGroup>
              <Label for="phase-quota">Number of qualified {units} *</Label>
              <Input id="phase-quota" type="number" min={minimum} max={context.capacity} step="1" value={quota}
                invalid={submitted && !validQuota} aria-describedby="phase-quota-help" onChange={event => setQuota(event.target.value)} />
              <small id="phase-quota-help" className="form-text text-muted">Between {minimum} and {context.capacity} {units}. There must be at least one qualifier per group.</small>
              {submitted && !validQuota && <div className="invalid-feedback d-block">Enter a whole number between {minimum} and {context.capacity}.</div>}
            </FormGroup>
            {drawSize != null && <Alert color="info">{drawSize} draw places · {drawSize - Number(quota)} BYEs</Alert>}
            <FormGroup>
              <Label for="phase-ranking">Inter-group ranking *</Label>
              <Input id="phase-ranking" type="select" value={ranking} onChange={event => setRanking(event.target.value)}>
                <option value="fixed">Fixed</option><option value="dynamic">Dynamic</option>
              </Input>
              <small className="form-text text-muted">Fixed preserves group positions. Dynamic compares qualifiers using the existing inter-group ranking rules.</small>
            </FormGroup>
            </>}
          </Col></Row>
        </fieldset>
        {operation.message && <Alert color={operation.status === "error" ? "danger" : "warning"}>{operation.message}</Alert>}
        {operation.status === "unknown" && <Alert color="warning">The result is not confirmed yet. <Button type="button" color="link" onClick={operation.recover}>Check saved result</Button></Alert>}
        <EditorActions dirty={dirty} pending={operation.pending} saveLabel={phaseId ? "Save changes" : "Create phase"}>
          <Link className="btn btn-light" to={returnTo}>Cancel</Link>
          <Button color="primary" type="submit" disabled={blocked || operation.pending || Boolean(phaseId && !dirty)}>
            {operation.pending ? "Saving phase…" : phaseId ? "Save changes" : "Create phase"}
          </Button>
        </EditorActions>
      </form>
    </CardBody></Card>
    {review && <PhaseActionDialog eventId={eventId} competitionId={competitionId} phaseId={phaseId} action="edit"
      details={details} pending={operation.pending} onRecover={operation.recover} message={operation.message}
      onClose={() => setReview(false)} onConfirm={(preview, confirmation) => operation.save("edit", preview.revision, preview.changes.after, confirmation)} />}
  </>
}

export default function Details({ noPageClass, match: { params: { id: eventId, competitionId, phaseId } = {} } = {} }) {
  const userId = useSelector(state => state.session?.userDetails?.id || state.session?.userDetails?._id)
  const context = usePhaseContext(eventId, competitionId)
  return <div className={noPageClass ? "flex-grow-1" : "page-content"}><Container fluid>
    {context.loading ? <Card><CardBody role="status">Loading phase details…</CardBody></Card> : context.error ?
      <Alert color="danger">Phase details could not be loaded. <Button color="link" onClick={context.reload}>Retry</Button></Alert> :
      <PhaseEditor key={`${eventId}:${competitionId}:${phaseId || "new"}`} context={context.data} phaseId={phaseId} userId={userId} />}
  </Container></div>
}
